import axios from 'axios';
import parse from 'html-react-parser';
import qs from 'qs';
import { type ReactNode,useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/core/Accordion';
import { Badge } from '@/components/core/Badge';
import { Loader } from '@/components/core/Loader';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/core/Select';
import { useTranslation } from '@/hooks/useTranslation';
import { restUrl } from '@/services/utils';

import {
    aggregatedComments,
    attachReviewer,
    type CommunicationChannel,
    descriptionExceedsTwoLines,
    formatCustomValue,
    formatReviewDate,
    gdsnIdsNeedingLookup,
    type GdsnParty,
    getFullReviewMessage,
    groupMessages,
    indexGdsnParties,
    type OrganizationContact,
    REVIEW_DESCRIPTION_LINES,
    reviewerName,
    reviewMessageKey,
    reviewStatusBadgeVariant,
    sanitizeReviewHtml,
    showsErrorGroups,
    showsWarningGroups,
    stripHtml,
    type SupplierReview,
    supplierReviewsUrl,
    VIEW_BY_ERRORS_WARNINGS,
    VIEW_BY_REVIEWER,
} from './supplierReviews';

interface ReceivedReviewsProps {
    primaryKey: string;
    publicationDestination?: string;
    reviewer?: string;
}

const ReceivedReviews = ({
    primaryKey,
    publicationDestination,
    reviewer,
}: ReceivedReviewsProps) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [reviews, setReviews] = useState<SupplierReview[]>([]);
    const [gdsnParties, setGdsnParties] = useState<Record<string, GdsnParty>>({});
    const [view, setView] = useState<string>(VIEW_BY_REVIEWER);
    const [showErrors, setShowErrors] = useState(false);
    const [showWarnings, setShowWarnings] = useState(false);
    const [openReviewers, setOpenReviewers] = useState<string[]>([]);
    const [openMessages, setOpenMessages] = useState<string[]>([]);
    const [expandedMessages, setExpandedMessages] = useState<Record<string, boolean>>({});

    useEffect(() => {
        let cancelled = false;

        (async function loadReviews() {
            setLoading(true);
            try {
                const reviewsResponse = await axios.get<SupplierReview[]>(
                    supplierReviewsUrl(primaryKey, { publicationDestination, reviewer }),
                );
                const loaded = Array.isArray(reviewsResponse.data) ? reviewsResponse.data : [];
                const [organizations, channels] = await Promise.all([
                    axios.get<OrganizationContact[]>(restUrl('contacts'))
                        .then((response) => Array.isArray(response.data) ? response.data : [])
                        .catch(() => [] as OrganizationContact[]),
                    axios.get<CommunicationChannel[]>(restUrl('communicationChannels'))
                        .then((response) => Array.isArray(response.data) ? response.data : [])
                        .catch(() => [] as CommunicationChannel[]),
                ]);
                const withReviewers = loaded.map((review) =>
                    attachReviewer(review, organizations, channels),
                );
                const ids = gdsnIdsNeedingLookup(withReviewers);
                let parties: Record<string, GdsnParty> = {};
                if (ids.length > 0) {
                    try {
                        const gdsnResponse = await axios.get<GdsnParty[]>(restUrl('gdsn/parties'), {
                            params: { gln: ids },
                            paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
                        });
                        parties = indexGdsnParties(Array.isArray(gdsnResponse.data) ? gdsnResponse.data : []);
                    } catch {
                        parties = {};
                    }
                }
                if (!cancelled) {
                    setReviews(withReviewers);
                    setGdsnParties(parties);
                    setOpenReviewers(withReviewers.length > 0 ? ['review-0'] : []);
                }
            } catch {
                if (!cancelled) {
                    setReviews([]);
                    setGdsnParties({});
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [primaryKey, publicationDestination, reviewer]);

    const reviewErrors = useMemo(() => groupMessages(reviews, 'reviewErrors'), [reviews]);
    const reviewWarnings = useMemo(() => groupMessages(reviews, 'reviewWarnings'), [reviews]);
    const errorCount = Object.keys(reviewErrors).length;
    const warningCount = Object.keys(reviewWarnings).length;

    if (loading) {
        return (
            <div className="tw-flex tw-justify-center tw-py-8" data-testid="received-reviews-loading">
                <Loader loading={true} />
            </div>
        );
    }

    return (
        <div className="tw-flex tw-h-full tw-flex-col" data-testid="received-reviews">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-pb-4">
                <div className="tw-flex tw-items-center">
                    {view === VIEW_BY_ERRORS_WARNINGS && (
                        <>
                            <button
                                type="button"
                                className={`tw-mr-2 tw-border-0 tw-bg-white tw-px-5 tw-py-1 tw-text-base tw-font-bold tw-text-[#ff5454] ${showErrors ? 'tw-bg-[#fbdfdf]' : ''}`}
                                onClick={() => setShowErrors((current) => !current)}
                            >
                                {errorCount} {t(errorCount === 1 ? 'ITEM_REVIEW.ERROR' : 'ITEM_REVIEW.ERRORS')}
                            </button>
                            <button
                                type="button"
                                className={`tw-mr-2 tw-border-0 tw-bg-white tw-px-5 tw-py-1 tw-text-base tw-font-bold tw-text-[#fabb3d] ${showWarnings ? 'tw-bg-[#f1efed]' : ''}`}
                                onClick={() => setShowWarnings((current) => !current)}
                            >
                                {warningCount} {t(warningCount === 1 ? 'ITEM_REVIEW.WARNING' : 'ITEM_REVIEW.WARNINGS')}
                            </button>
                        </>
                    )}
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <span>{t('ITEM_REVIEW.GROUP_BY')}:</span>
                    <Select value={view} onValueChange={setView}>
                        <SelectTrigger className="tw-w-52" aria-label={t('ITEM_REVIEW.GROUP_BY')}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={VIEW_BY_REVIEWER}>{t('ITEM_REVIEW.REVIEWER')}</SelectItem>
                            <SelectItem value={VIEW_BY_ERRORS_WARNINGS}>{t('ITEM_REVIEW.ERRORS_WARNINGS')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {view === VIEW_BY_REVIEWER && (
                <Accordion
                    type="multiple"
                    value={openReviewers}
                    onValueChange={setOpenReviewers}
                    className="tw-overflow-y-auto"
                >
                    {reviews.map((review, index) => (
                        <AccordionItem
                            key={`review-${index}`}
                            value={`review-${index}`}
                            className="tw-mb-2 tw-border tw-border-solid tw-border-[#eceaea] tw-bg-white tw-shadow-md data-[state=open]:tw-border-[#2AD2C5]"
                        >
                            <AccordionTrigger className="tw-px-3 tw-py-2">
                                <ReviewerHeading review={review} parties={gdsnParties} />
                            </AccordionTrigger>
                            <AccordionContent className="tw-px-3 tw-pb-4">
                                <ReviewerBody
                                    review={review}
                                    expandedMessages={expandedMessages}
                                    onToggleMessage={(key) => setExpandedMessages((current) => ({
                                        ...current,
                                        [key]: !current[key],
                                    }))}
                                />
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            )}

            {view === VIEW_BY_ERRORS_WARNINGS && (
                <Accordion
                    type="multiple"
                    value={openMessages}
                    onValueChange={setOpenMessages}
                    className="tw-overflow-y-auto"
                >
                    {showsErrorGroups(showErrors, showWarnings) && Object.entries(reviewErrors).map(([message, grouped]) => (
                        <MessageGroup
                            key={`error-${message}`}
                            kind="error"
                            message={message}
                            reviews={grouped}
                            parties={gdsnParties}
                        />
                    ))}
                    {showsWarningGroups(showErrors, showWarnings) && Object.entries(reviewWarnings).map(([message, grouped]) => (
                        <MessageGroup
                            key={`warning-${message}`}
                            kind="warning"
                            message={message}
                            reviews={grouped}
                            parties={gdsnParties}
                        />
                    ))}
                </Accordion>
            )}
        </div>
    );
};

const ReviewerLabel = ({
    review,
    parties,
}: {
    review: SupplierReview;
    parties: Record<string, GdsnParty>;
}) => {
    const name = reviewerName(review, parties);
    const id = review.reviewer__?.id;
    if (name) {
        return (
            <span>
                {name}
                {id ? ` (${id})` : ''}
            </span>
        );
    }
    return <span>{id}</span>;
};

const ReviewerHeading = ({
    review,
    parties,
}: {
    review: SupplierReview;
    parties: Record<string, GdsnParty>;
}) => (
    <div className="tw-text-left">
        <div className="tw-flex tw-items-center tw-gap-1">
            <span className="tw-inline-block tw-text-t-sm tw-text-t-primary lg:tw-text-t-base tw-font-bold">
                <ReviewerLabel review={review} parties={parties} />
            </span>
            {review.reviewStatus && (
                <Badge variant={reviewStatusBadgeVariant(review.reviewStatus)}>
                    {review.reviewStatus}
                </Badge>
            )}
        </div>
        <div className="tw-text-t-sm tw-text-t-inactive lg:tw-text-t-base">{formatReviewDate(review.date)}</div>
    </div>
);

const CustomValueRows = ({ values }: { values?: Record<string, unknown> }) => {
    if (!values) {
        return null;
    }
    return (
        <>
            {Object.entries(values).map(([code, value]) => (
                <div key={code} className="tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                    {code}: {formatCustomValue(value)}
                </div>
            ))}
        </>
    );
};

function useTwoLineOverflow(contentKey: string) {
    const ref = useRef<HTMLDivElement>(null);
    const [overflows, setOverflows] = useState<boolean | null>(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) {
            return undefined;
        }

        const measure = () => {
            if (el.clientWidth === 0) {
                setOverflows(null);
                return;
            }
            const style = getComputedStyle(el);
            let lineHeight = Number.parseFloat(style.lineHeight);
            if (!Number.isFinite(lineHeight)) {
                const fontSize = Number.parseFloat(style.fontSize);
                lineHeight = Number.isFinite(fontSize) ? fontSize * 1.2 : 0;
            }
            if (!lineHeight) {
                setOverflows(null);
                return;
            }
            // Measure this element. A clone wraps to fewer lines than the text
            // in the review column, even when it copies the font and the width.
            const wasClamped = el.classList.contains('tw-line-clamp-2');
            if (wasClamped) {
                el.classList.remove('tw-line-clamp-2');
            }
            const height = el.scrollHeight;
            if (wasClamped) {
                el.classList.add('tw-line-clamp-2');
            }
            if (height === 0) {
                setOverflows(null);
                return;
            }
            setOverflows(height > lineHeight * REVIEW_DESCRIPTION_LINES + 1);
        };

        measure();
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [contentKey]);

    return { ref, overflows };
}

const ShowMoreButton = ({ expanded, onClick }: { expanded: boolean; onClick: () => void }) => {
    const { t } = useTranslation();
    return (
        <button
            type="button"
            className="tw-mt-1 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-[#318DAC]"
            onClick={onClick}
        >
            {t(expanded ? 'SHOW_LESS' : 'SHOW_MORE')}
        </button>
    );
};

const ClampedDescription = ({
    contentKey,
    expanded,
    onToggle,
    fallbackText,
    children,
}: {
    contentKey: string;
    expanded: boolean;
    onToggle: () => void;
    fallbackText: string;
    children: ReactNode;
}) => {
    const { ref, overflows } = useTwoLineOverflow(`${contentKey}:${expanded}`);
    const canToggle = overflows ?? descriptionExceedsTwoLines(fallbackText);

    return (
        <div>
            <div
                ref={ref}
                className={canToggle && !expanded ? 'tw-line-clamp-2 tw-break-words' : 'tw-break-words'}
            >
                {children}
            </div>
            {canToggle && <ShowMoreButton expanded={expanded} onClick={onToggle} />}
        </div>
    );
};

const ExpandableMessage = ({
    review,
    message,
    type,
    index,
    expanded,
    tone,
    onToggle,
}: {
    review: SupplierReview;
    message: string;
    type: string;
    index: number;
    expanded: boolean;
    tone: 'error' | 'warning' | 'remark';
    onToggle: (key: string) => void;
}) => {
    const key = reviewMessageKey(review, type, index);
    const text = getFullReviewMessage(review, message) || stripHtml(message);
    const border = tone === 'error'
        ? 'tw-border-l-[#ff5454]'
        : tone === 'warning'
            ? 'tw-border-l-[#fabb3d]'
            : 'tw-border-l-[#d9d9d9]';

    return (
        <div className={`tw-text-t-sm tw-text-t-primary lg:tw-text-t-base tw-my-1 tw-border-t-0 tw-border-r-0 tw-border-b-0 tw-border-l tw-border-solid tw-pl-2 ${border}`}>
            <ClampedDescription
                contentKey={key}
                expanded={expanded}
                onToggle={() => onToggle(key)}
                fallbackText={text}
            >
                {text}
            </ClampedDescription>
        </div>
    );
};

const ReviewerBody = ({
    review,
    expandedMessages,
    onToggleMessage,
}: {
    review: SupplierReview;
    expandedMessages: Record<string, boolean>;
    onToggleMessage: (key: string) => void;
}) => {
    const { t } = useTranslation();

    return (
        <div className="tw-flex tw-flex-col tw-gap-6">
            {review.customValues && (
                <div className="tw-grid tw-grid-cols-12 tw-gap-2">
                    <div className="tw-col-span-3 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base tw-font-bold">{t('ITEM_REVIEW.CUSTOM_VALUES')}</div>
                    <div className="tw-col-span-9">
                        <CustomValueRows values={review.customValues} />
                    </div>
                </div>
            )}
            {review.reviewErrors && review.reviewErrors.length > 0 && (
                <div className="tw-grid tw-grid-cols-12 tw-gap-2">
                    <div className="tw-col-span-3 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base tw-font-bold ">{t('ITEM_REVIEW.ERRORS')}</div>
                    <div className="tw-col-span-9">
                        {review.reviewErrors.map((error, index) => (
                            <ExpandableMessage
                                key={reviewMessageKey(review, 'error', index)}
                                review={review}
                                message={error}
                                type="error"
                                index={index}
                                expanded={Boolean(expandedMessages[reviewMessageKey(review, 'error', index)])}
                                tone="error"
                                onToggle={onToggleMessage}
                            />
                        ))}
                    </div>
                </div>
            )}
            {review.reviewWarnings && review.reviewWarnings.length > 0 && (
                <div className="tw-grid tw-grid-cols-12 tw-gap-2">
                    <div className="tw-col-span-3 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base tw-font-bold">{t('ITEM_REVIEW.WARNINGS')}</div>
                    <div className="tw-col-span-9">
                        {review.reviewWarnings.map((warning, index) => (
                            <ExpandableMessage
                                key={reviewMessageKey(review, 'warning', index)}
                                review={review}
                                message={warning}
                                type="warning"
                                index={index}
                                expanded={Boolean(expandedMessages[reviewMessageKey(review, 'warning', index)])}
                                tone="warning"
                                onToggle={onToggleMessage}
                            />
                        ))}
                    </div>
                </div>
            )}
            {review.reviewRemarks && review.reviewRemarks.length > 0 && (
                <div className="tw-grid tw-grid-cols-12 tw-gap-2">
                    <div className="tw-col-span-3 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base tw-font-bold">{t('ITEM_REVIEW.REMARKS')}</div>
                    <div className="tw-col-span-9">
                        {review.reviewRemarks.map((remark, index) => (
                            <ExpandableMessage
                                key={reviewMessageKey(review, 'remark', index)}
                                review={review}
                                message={remark}
                                type="remark"
                                index={index}
                                expanded={Boolean(expandedMessages[reviewMessageKey(review, 'remark', index)])}
                                tone="remark"
                                onToggle={onToggleMessage}
                            />
                        ))}
                    </div>
                </div>
            )}
            {review.combinedComment && (
                <div className="tw-grid tw-grid-cols-12 tw-gap-2" id={`comments-${review.reviewer__?.id ?? ''}`}>
                    <div className="tw-col-span-3 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base tw-font-bold">{t('ITEM_REVIEW.COMMENT')}</div>
                    <div className="comment-body tw-col-span-9">
                        <ClampedDescription
                            contentKey={reviewMessageKey(review, 'comment', 0)}
                            expanded={Boolean(expandedMessages[reviewMessageKey(review, 'comment', 0)])}
                            onToggle={() => onToggleMessage(reviewMessageKey(review, 'comment', 0))}
                            fallbackText={review.combinedComment}
                        >
                            {parse(sanitizeReviewHtml(review.combinedComment))}
                        </ClampedDescription>
                    </div>
                </div>
            )}
        </div>
    );
};

const MessageGroup = ({
    kind,
    message,
    reviews,
    parties,
}: {
    kind: 'error' | 'warning';
    message: string;
    reviews: SupplierReview[];
    parties: Record<string, GdsnParty>;
}) => {
    const { t } = useTranslation();
    const comments = aggregatedComments(reviews);
    const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({});
    const border = kind === 'error' ? 'tw-border-l-[#ff5454]' : 'tw-border-l-[#fabb3d]';
    const paragraph = kind === 'error' ? 'tw-border-l-[#ff5454]' : 'tw-border-l-[#fabb3d]';
    const fullMessage = reviews[0] ? getFullReviewMessage(reviews[0], message) : message;

    return (
        <AccordionItem
            value={`${kind}-${message}`}
            className={`tw-mb-2 tw-border tw-border-solid tw-border-[#eceaea] tw-bg-white tw-shadow-md data-[state=open]:tw-border-[#2AD2C5] ${border}`}
        >
            <AccordionTrigger className="tw-px-3 tw-py-2">
                <span className="tw-line-clamp-2 tw-min-w-0 tw-flex-1 tw-break-words tw-text-left">
                    {fullMessage}
                    {reviews.length > 1 ? <span className="tw-ml-2">x{reviews.length}</span> : null}
                </span>
            </AccordionTrigger>
            <AccordionContent className="tw-px-3 tw-pb-4">
                <div className={`tw-my-2 tw-break-words tw-border-t-0 tw-border-r-0 tw-border-b-0 tw-border-l tw-border-solid tw-pl-2 ${paragraph}`}>{fullMessage}</div>
                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                    <div>
                        <p className="tw-text-t-sm tw-text-t-primary lg:tw-text-t-base tw-font-bold">{t('ITEM_REVIEW.REPORTED_BY')}</p>
                        <table>
                            <tbody>
                                {reviews.map((review, index) => (
                                    <tr key={index}>
                                        <td className="tw-pr-2">
                                            <ReviewerLabel review={review} parties={parties} />
                                        </td>
                                        <td className="tw-pr-2">-</td>
                                        <td className="tw-pr-2">{formatReviewDate(review.date)}</td>
                                        <td className="tw-px-2">
                                            {review.reviewStatus && (
                                                <Badge variant={reviewStatusBadgeVariant(review.reviewStatus)}>{review.reviewStatus}</Badge>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {reviews[0]?.customValues && (
                        <div>
                            <p className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base tw-font-bold">{t('ITEM_REVIEW.DETAILS')}</p>
                            <CustomValueRows values={reviews[0].customValues} />
                        </div>
                    )}
                </div>
                {comments.length > 0 && (
                    <div className="tw-mt-4">
                        <p className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base tw-font-bold">{t('ITEM_REVIEW.COMMENTS')}</p>
                        {comments.map((review, index) => {
                            const comment = review.combinedComment || review.comment || review.generatedComment || '';
                            const key = reviewMessageKey(review, 'group-comment', index);
                            return (
                                <div key={key} className="tw-grid tw-grid-cols-12 tw-gap-2 tw-py-1">
                                    <div className="tw-col-span-3">
                                        <ReviewerLabel review={review} parties={parties} />
                                    </div>
                                    <div className="comment-body tw-col-span-9">
                                        <ClampedDescription
                                            contentKey={key}
                                            expanded={Boolean(expandedComments[key])}
                                            onToggle={() => setExpandedComments((current) => ({
                                                ...current,
                                                [key]: !current[key],
                                            }))}
                                            fallbackText={comment}
                                        >
                                            {parse(sanitizeReviewHtml(comment))}
                                        </ClampedDescription>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </AccordionContent>
        </AccordionItem>
    );
};

export { ReceivedReviews };
